import { assignSignatureRequestSignerColors } from "../signatureRequestSigners";
import { getSignatureRequestVariableSignerSlots } from "../signatureRequestVariableUtils";
import type { DirectoryUser } from "@/lib/types";
import SelectContactModal from "@/components/features/contact/SelectContactModal/SelectContactModal";
import {
  Button,
  Checkbox,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Typography,
} from "@/components/ui";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/shadcn/Tooltip/Tooltip";
import type { Contact } from "@/lib/types/Contact";
import { cn } from "@/lib/utils/cn";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { ChevronDown, CircleHelp, Plus } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getNextSignatureRequestSignerSlotIndex,
  getSignatureRequestSignerId,
  isContactSigner,
  isUserSigner,
  type SignatureRequestSigner,
} from "../signatureRequestSigners";
import type {
  LayoutField,
  PendingSignerSlot,
} from "../SignatureRequestPdfLayoutEditor.constants";
import { ContactSignerRow } from "./ContactSignerRow";
import { PlaceFieldsStep } from "./PlaceFieldsStep";
import SelectUserModal from "./SelectUserSlideover";
import type { SignatureRequestPreviewDocument } from "./SignatureRequestDocumentPreview";
import { UserSignerRow } from "./UserSignerRow";

interface SendRequestStepProps {
  documentVariables: string[];
  matterId: string;
  previewDocument: SignatureRequestPreviewDocument | null;
  signers: SignatureRequestSigner[];
  setSigners: React.Dispatch<React.SetStateAction<SignatureRequestSigner[]>>;
  recipientSlotOrder: string[];
  setRecipientSlotOrder: React.Dispatch<React.SetStateAction<string[]>>;
  isOrdered: boolean;
  setIsOrdered: React.Dispatch<React.SetStateAction<boolean>>;
  layoutFields: LayoutField[];
  onLayoutFieldsChange: (fields: LayoutField[]) => void;
}

export const SendRequestStep = ({
  documentVariables,
  matterId,
  previewDocument,
  signers,
  setSigners,
  recipientSlotOrder,
  setRecipientSlotOrder,
  isOrdered,
  setIsOrdered,
  layoutFields,
  onLayoutFieldsChange,
}: SendRequestStepProps) => {
  const [isSelectContactModalOpen, setIsSelectContactModalOpen] =
    useState(false);
  const [isSelectUserSlideoverOpen, setIsSelectUserSlideoverOpen] =
    useState(false);
  const [activeSignerId, setActiveSignerId] = useState("");
  const [pendingSignerSlots, setPendingSignerSlots] = useState<
    PendingSignerSlot[]
  >([]);
  const [templateSignerSlots, setTemplateSignerSlots] = useState<
    PendingSignerSlot[]
  >([]);
  const handlePendingSignerSlotsChange = useCallback(
    (pending: PendingSignerSlot[], template: PendingSignerSlot[]) => {
      setPendingSignerSlots(pending);
      setTemplateSignerSlots(template);
    },
    [],
  );
  const reservedSlots = useMemo(
    () => [
      ...getSignatureRequestVariableSignerSlots(documentVariables),
      ...templateSignerSlots,
    ],
    [documentVariables, templateSignerSlots],
  );
  const [requestedSignerSlot, setRequestedSignerSlot] = useState<{
    index: number;
    kind: SignatureRequestSigner["kind"];
  } | null>(null);
  const recipientSlotKeys = useMemo(() => {
    const currentKeys = [
      ...signers.map((signer) => `${signer.kind}:${signer.slotIndex}`),
      ...pendingSignerSlots.map((slot) => `${slot.kind}:${slot.index}`),
    ];
    return [...new Set([...recipientSlotOrder, ...currentKeys])];
  }, [pendingSignerSlots, recipientSlotOrder, signers]);
  useEffect(() => {
    if (recipientSlotKeys.length !== recipientSlotOrder.length) {
      setRecipientSlotOrder(recipientSlotKeys);
    }
  }, [recipientSlotKeys, recipientSlotOrder.length, setRecipientSlotOrder]);
  useEffect(() => {
    setSigners((current) => {
      const sorted = [...current].sort(
        (a, b) =>
          recipientSlotKeys.indexOf(`${a.kind}:${a.slotIndex}`) -
          recipientSlotKeys.indexOf(`${b.kind}:${b.slotIndex}`),
      );
      const colored = assignSignatureRequestSignerColors(sorted, reservedSlots);
      return colored.every((signer, index) => signer === current[index])
        ? current
        : colored;
    });
  }, [recipientSlotKeys, reservedSlots, setSigners]);

  const signerIds = useMemo(
    () => signers.map((signer) => getSignatureRequestSignerId(signer)),
    [signers],
  );
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
  );

  useEffect(() => {
    if (signerIds.includes(activeSignerId)) return;
    setActiveSignerId(signerIds[0] ?? "");
  }, [activeSignerId, signerIds]);

  const currentContactSigners = useMemo(
    () => signers.filter(isContactSigner).map((signer) => signer.contact),
    [signers],
  );
  const currentUserSigners = useMemo(
    () => signers.filter(isUserSigner).map((signer) => signer.user),
    [signers],
  );
  const currentSignerIds = currentContactSigners.map((signer) => signer.id);
  const currentUserIds = currentUserSigners.map((signer) => signer.id);
  const removeSignerAtIndex = (index: number) => {
    const signer = signers[index];
    const signerId = getSignatureRequestSignerId(signer);
    onLayoutFieldsChange(
      layoutFields.filter((field) => field.signerId !== signerId),
    );
    const remaining = signers.filter((_, itemIndex) => itemIndex !== index);
    const reserved = new Set(
      reservedSlots
        .filter((slot) => slot.kind === signer.kind)
        .map((slot) => slot.index),
    );
    const replacements = new Map<string, number>();
    const occupied = new Set(reserved);
    remaining
      .filter((item) => item.kind === signer.kind)
      .sort((a, b) => a.slotIndex - b.slotIndex)
      .forEach((item) => {
        if (reserved.has(item.slotIndex)) return;
        let nextIndex = 0;
        while (occupied.has(nextIndex)) nextIndex += 1;
        occupied.add(nextIndex);
        replacements.set(`${item.kind}:${item.slotIndex}`, nextIndex);
      });
    const removedKey = `${signer.kind}:${signer.slotIndex}`;
    setRecipientSlotOrder(
      recipientSlotKeys
        .filter((key) => key !== removedKey || reserved.has(signer.slotIndex))
        .map((key) => {
          const replacement = replacements.get(key);
          return replacement === undefined
            ? key
            : `${signer.kind}:${replacement}`;
        }),
    );
    setSigners(
      remaining.map((item) => ({
        ...item,
        slotIndex:
          replacements.get(`${item.kind}:${item.slotIndex}`) ?? item.slotIndex,
      })),
    );
  };
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = signers.findIndex(
      (signer) => getSignatureRequestSignerId(signer) === active.id,
    );
    const newIndex = signers.findIndex(
      (signer) => getSignatureRequestSignerId(signer) === over.id,
    );

    if (oldIndex < 0 || newIndex < 0) {
      return;
    }

    const fromKey = `${signers[oldIndex].kind}:${signers[oldIndex].slotIndex}`;
    const toKey = `${signers[newIndex].kind}:${signers[newIndex].slotIndex}`;
    setRecipientSlotOrder(
      arrayMove(
        recipientSlotKeys,
        recipientSlotKeys.indexOf(fromKey),
        recipientSlotKeys.indexOf(toKey),
      ),
    );
    setSigners((prev) => arrayMove(prev, oldIndex, newIndex));
  };

  const sortBySlotOrder = (items: SignatureRequestSigner[]) => {
    const position = (signer: SignatureRequestSigner) => {
      const index = recipientSlotKeys.indexOf(
        `${signer.kind}:${signer.slotIndex}`,
      );
      return index < 0 ? Number.POSITIVE_INFINITY : index;
    };
    return [...items].sort((a, b) => position(a) - position(b));
  };

  const handleSelectContacts = (contacts: Contact[]) => {
    setSigners((prev) => {
      const next = [...prev];
      contacts.forEach((contact) => {
        next.push({
          kind: "contact",
          contact,
          slotIndex: getNextSignatureRequestSignerSlotIndex(next, "contact"),
        });
      });
      return sortBySlotOrder(next);
    });
    setIsSelectContactModalOpen(false);
  };

  const addSignerForSlot = (signer: SignatureRequestSigner) => {
    setSigners((previous) => sortBySlotOrder([...previous, signer]));
  };

  const handleSelectContactForSlot = (contact: Contact) => {
    if (!requestedSignerSlot || requestedSignerSlot.kind !== "contact") return;
    addSignerForSlot({
      kind: "contact",
      contact,
      slotIndex: requestedSignerSlot.index,
    });
    setActiveSignerId(contact.id);
    setRequestedSignerSlot(null);
    setIsSelectContactModalOpen(false);
  };

  const openContactSelector = (slotIndex?: number) => {
    setRequestedSignerSlot(
      slotIndex === undefined ? null : { index: slotIndex, kind: "contact" },
    );
    setIsSelectContactModalOpen(true);
  };

  const openUserSelector = (slotIndex?: number) => {
    setRequestedSignerSlot(
      slotIndex === undefined ? null : { index: slotIndex, kind: "user" },
    );
    setIsSelectUserSlideoverOpen(true);
  };

  const handleSelectUsers = (users: DirectoryUser[]) => {
    setSigners((prev) => {
      const next = [...prev];
      users.forEach((user) => {
        next.push({
          kind: "user",
          user,
          slotIndex: getNextSignatureRequestSignerSlotIndex(next, "user"),
        });
      });
      return sortBySlotOrder(next);
    });
    setIsSelectUserSlideoverOpen(false);
  };

  const handleSelectUserForSlot = (user: DirectoryUser) => {
    if (!requestedSignerSlot || requestedSignerSlot.kind !== "user") return;
    addSignerForSlot({
      kind: "user",
      user,
      slotIndex: requestedSignerSlot.index,
    });
    setActiveSignerId(user.id);
    setRequestedSignerSlot(null);
    setIsSelectUserSlideoverOpen(false);
  };

  return (
    <div className="bg-card grid h-full min-h-0 w-full overflow-hidden lg:grid-cols-[320px_minmax(0,1fr)]">
      <aside className="border-border/80 bg-card flex min-h-0 flex-col border-r">
        <div className="border-border/80 flex items-center justify-between gap-2 border-b p-3">
          <Typography className="text-base font-semibold">
            Recipients
          </Typography>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1.5"
              >
                <Plus className="h-4 w-4" />
                Add signer
                <ChevronDown className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onSelect={() => {
                  openContactSelector();
                }}
              >
                Add contact signer
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => {
                  openUserSelector();
                }}
              >
                Add firm signer
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="border-border/80 flex items-center gap-2 border-b px-3 py-2.5">
          <Checkbox
            checked={isOrdered}
            onCheckedChange={(checked) => {
              setIsOrdered(checked === true);
            }}
            aria-label="Ordered signing"
          />
          <Typography className="text-sm font-medium">Ordered</Typography>
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="text-muted-foreground inline-flex items-center"
                  aria-label="Ordering help"
                >
                  <CircleHelp className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">
                Only one signer can sign at a time when ordered is on
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {signers.length === 0 && pendingSignerSlots.length === 0 ? (
            <div className="p-4">
              <Typography className="text-sm font-medium">
                No signers added
              </Typography>
            </div>
          ) : (
            <>
              <DndContext
                collisionDetection={closestCenter}
                sensors={sensors}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={signerIds}
                  strategy={verticalListSortingStrategy}
                >
                  {recipientSlotKeys.map((key) => {
                    const index = signers.findIndex(
                      (signer) => `${signer.kind}:${signer.slotIndex}` === key,
                    );
                    if (index >= 0) {
                      const signer = signers[index];
                      return isContactSigner(signer) ? (
                        <ContactSignerRow
                          key={`signer-${signer.contact.id}-${index}`}
                          contact={signer.contact}
                          signerId={getSignatureRequestSignerId(signer)}
                          hasFields={layoutFields.some(
                            (field) =>
                              field.signerId ===
                              getSignatureRequestSignerId(signer),
                          )}
                          index={index}
                          colorIndex={signer.colorIndex}
                          slotIndex={signer.slotIndex}
                          isActive={activeSignerId === signer.contact.id}
                          onSelect={() => {
                            setActiveSignerId(signer.contact.id);
                          }}
                          isOrdered={isOrdered}
                          currentSignerCount={
                            signers.length + pendingSignerSlots.length
                          }
                          onRemove={() => {
                            removeSignerAtIndex(index);
                          }}
                        />
                      ) : (
                        <UserSignerRow
                          key={`signer-${signer.user.id}-${index}`}
                          user={signer.user}
                          signerId={getSignatureRequestSignerId(signer)}
                          hasFields={layoutFields.some(
                            (field) =>
                              field.signerId ===
                              getSignatureRequestSignerId(signer),
                          )}
                          index={index}
                          colorIndex={signer.colorIndex}
                          slotIndex={signer.slotIndex}
                          isActive={activeSignerId === signer.user.id}
                          onSelect={() => {
                            setActiveSignerId(signer.user.id);
                          }}
                          isOrdered={isOrdered}
                          currentSignerCount={
                            signers.length + pendingSignerSlots.length
                          }
                          onRemove={() => {
                            removeSignerAtIndex(index);
                          }}
                        />
                      );
                    }
                    const slot = pendingSignerSlots.find(
                      (slot) => `${slot.kind}:${slot.index}` === key,
                    );
                    if (!slot) return null;
                    const label =
                      slot.kind === "contact"
                        ? `Signer ${slot.index + 1}`
                        : `Firm signer ${slot.index + 1}`;

                    return (
                      <button
                        key={`${slot.kind}:${slot.index}`}
                        type="button"
                        className={cn(
                          "hover:bg-muted/40 flex min-h-16 w-full items-center gap-3 px-4 py-3 text-left transition-colors",
                          "border-b",
                        )}
                        aria-label={`Add ${label}`}
                        onClick={() => {
                          if (slot.kind === "contact") {
                            openContactSelector(slot.index);
                          } else {
                            openUserSelector(slot.index);
                          }
                        }}
                      >
                        <span className="text-muted-foreground flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-dashed">
                          <Plus className="h-4 w-4" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-semibold">
                            {label}
                          </span>
                          <span className="text-muted-foreground block text-xs">
                            Add recipient · {slot.fieldCount}{" "}
                            {slot.fieldCount === 1 ? "field" : "fields"}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </SortableContext>
              </DndContext>
            </>
          )}
        </div>
      </aside>

      <div className="min-h-0 min-w-0 bg-slate-50/40">
        <PlaceFieldsStep
          previewDocument={previewDocument}
          signers={signers}
          layoutFields={layoutFields}
          onLayoutFieldsChange={onLayoutFieldsChange}
          activeSignerId={activeSignerId}
          onActiveSignerChange={setActiveSignerId}
          onPendingSignerSlotsChange={handlePendingSignerSlotsChange}
          onRequestSignerSlot={(slot) => {
            if (slot.kind === "contact") {
              openContactSelector(slot.index);
            } else {
              openUserSelector(slot.index);
            }
          }}
        />
      </div>

      <SelectContactModal
        open={isSelectContactModalOpen}
        setOpen={setIsSelectContactModalOpen}
        onSelectContact={handleSelectContactForSlot}
        onSelectContacts={
          requestedSignerSlot === null ? handleSelectContacts : undefined
        }
        multi={requestedSignerSlot === null}
        matterId={matterId || undefined}
        excludeContactIds={currentSignerIds}
        title="Select contact signer"
        description=""
      />
      <SelectUserModal
        matterId={matterId || undefined}
        open={isSelectUserSlideoverOpen}
        setOpen={setIsSelectUserSlideoverOpen}
        onSelectUsers={
          requestedSignerSlot === null ? handleSelectUsers : undefined
        }
        onSelectUser={handleSelectUserForSlot}
        multi={requestedSignerSlot === null}
        excludeUserIds={currentUserIds}
        title="Select firm signer"
        description=""
      />
    </div>
  );
};
